import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import './CommonAGGridTable.css';
import Loader from '../Utility/Loader/Loader';
import TextInputCell from './cells/TextInputCell';
import CurrencyInputCell from './cells/CurrencyInputCell';
import SelectInputCell from './cells/SelectInputCell';
import CheckboxCell from './cells/CheckboxCell';
import RadioCell from './cells/RadioCell';
import ReadonlyTextCell from './cells/ReadonlyTextCell';
import TotalsFooterCell from './cells/TotalsFooterCell';
import RowActionsCell from './cells/RowActionsCell';
import ExpandChevronCell from './cells/ExpandChevronCell';
import SortableHeader from './headers/SortableHeader';
import { getTruncatedCellTooltip, GRID_TOOLTIP_SHOW_DELAY_MS } from '../../utils/grid-cell-tooltip';

ModuleRegistry.registerModules([AllEnterpriseModule]);

const ENV_LICENSE = process.env.REACT_APP_AG_GRID_LICENSE;
if (ENV_LICENSE) LicenseManager.setLicenseKey(ENV_LICENSE);

const CELL_RENDERERS = {
  text: TextInputCell,
  currency: CurrencyInputCell,
  select: SelectInputCell,
  checkbox: CheckboxCell,
  radio: RadioCell,
  readonlyText: ReadonlyTextCell,
  totalsFooter: TotalsFooterCell,
};

const isFooterRow = (data) => Boolean(data && data.__cagFooter);

/* Stable empty-object identity for the gridOptions default. A `{}` literal default
   would be a fresh reference every render, needlessly invalidating mergedGridOptions. */
const EMPTY_GRID_OPTIONS = {};

/* Cell types that accept a drag-down (fill-handle) value when enableFillHandle is on.
   radio is excluded — a single choice copied across rows is meaningless; readonlyText,
   totalsFooter and structural columns are non-editable so the handle can never write to them. */
const FILL_ELIGIBLE_CELL_TYPES = new Set(['text', 'currency', 'select', 'checkbox']);

/* Focusable editable controls inside the grid, in DOM (row-major, left-to-right)
   order. Excludes hidden, disabled and read-only inputs so Tab visits only fields
   the user can actually edit. react-select renders a real text input that matches,
   so dropdown cells take part in the same sequence. */
const TAB_TARGET_SELECTOR = 'input:not([type="hidden"]):not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly])';

/* Header-name-fit width: measures the header label against the header font so a
   column is sized to its title (never the cell content) when autoSizeColumns is on.
   Clamped to the column's minWidth so short titles stay readable. */
let headerMeasureCtx;
const headerFitWidth = (label, required, minWidth) => {
  const floor = minWidth || 120;
  if (!label || typeof document === 'undefined') return floor;
  if (!headerMeasureCtx) {
    headerMeasureCtx = document.createElement('canvas').getContext('2d');
    headerMeasureCtx.font = "600 13px 'Aktiv Grotesk Medium', Arial, sans-serif";
  }
  const HORIZONTAL_PADDING = 24; /* matches .ag-header-cell horizontal padding */
  const SORT_ICON_ROOM = 18;
  const REQUIRED_ASTERISK = required ? 12 : 0;
  const measured = Math.ceil(headerMeasureCtx.measureText(label).width) + HORIZONTAL_PADDING + SORT_ICON_ROOM + REQUIRED_ASTERISK;
  return Math.max(measured, floor);
};

/**
 * Generic inline-editable grid built on ag-grid-react.
 *
 * Each user column may declare:
 *   field             string  - row property to read/write
 *   headerName        string
 *   cellType          'text' | 'currency' | 'select' | 'checkbox' | 'radio' | 'readonlyText' | 'totalsFooter'
 *   cellRenderer      custom React component (overrides cellType)
 *   cellProps         object forwarded to the cell renderer
 *   required          boolean - shows red asterisk in header
 *   sortable          boolean (default true)
 *   width / minWidth / maxWidth
 *   getCellDisabled   (row) => boolean   per-row disabled
 *   getCellReadOnly   (row) => boolean   per-row read-only (renders ReadonlyTextCell for this cell only)
 *   getCellError      (row) => string|undefined   error text under the input
 *   getCellClass      (row) => string|string[]   composes with AG-Grid cellClass
 *   fillable          boolean - opt a custom-renderer column into the fill handle (needs a `field`)
 *   footerCellProps   object  cellRendererParams override applied only when row is the pinned footer
 *
 * @param {object}   props
 * @param {Array}    props.columnDefs
 * @param {Array}    props.rowData
 * @param {Function} [props.getRowId]            (row) => string. Defaults to row.id.
 * @param {Function} [props.onRowChange]         (rowId, field, value) => void
 * @param {Function} [props.onAddRow]            () => void.   Renders trailing "+" on last row.
 * @param {Function} [props.onDeleteRow]         (rowId) => void. Renders trailing trash icon.
 * @param {boolean}  [props.readOnly=false]      Whole-table view mode — every editable cell renders as readonlyText.
 * @param {Function} [props.isRowReadOnly]       (row) => boolean   per-row view mode
 * @param {Function} [props.canAddRow]           (row) => boolean   hides "+" on the last row when false
 * @param {Function} [props.canDeleteRow]        (row) => boolean   hides trash per row when false
 * @param {Function} [props.getRowClass]         (row) => string|string[]   composes with AG-Grid rowClass
 * @param {Array}    [props.pinnedBottomRowData] AG-Grid pinned footer rows; rows tagged with __cagFooter: true get footerCellProps applied
 * @param {string}   [props.currencySymbol='£']
 * @param {Function} [props.detailRowRenderer]   master/detail body component
 * @param {Function} [props.isRowMaster]         (row) => boolean
 * @param {boolean}  [props.treeData=false]      AG-Grid tree-data mode (child rows share the parent's columns).
 *                                               Mutually exclusive with detailRowRenderer.
 * @param {Function} [props.getDataPath]         Required with treeData: (row) => string[]   path from root to row
 * @param {object}   [props.autoGroupColumnDef]  Override for the AG-Grid auto-group column (chevron column).
 *                                               Default: 40px, no header, no group label.
 * @param {number}   [props.groupDefaultExpanded=0] Initial tree-expansion depth (0 = all collapsed, -1 = all expanded).
 * @param {string}   [props.radioGroupName]      shared radio name (when using cellType: 'radio')
 * @param {string}   [props.selectedRadioRowId]  controlled radio selection
 * @param {string}   [props.height]              wrapper height; omit for autoHeight
 * @param {Function} [props.onLoadMore]          opt-in infinite scroll: called during vertical scroll once the
 *                                                 viewport passes ~75% of the loaded content (prefetch ahead of the bottom)
 * @param {boolean}  [props.hasMore=false]       whether another page is available (gates onLoadMore)
 * @param {boolean}  [props.loadingMore=false]   suppresses onLoadMore while a page is in flight and
 *                                                 renders a bottom-anchored loader inside the grid area
 * @param {boolean}  [props.autoSizeColumns=false] auto-size columns so every header label fits without truncation
 *                                                 (structural columns keep their fixed min/max widths)
 * @param {boolean}  [props.revealHeaderControlsOnHover=false] hide the column separator + kebab menu by default
 *                                                 and reveal them only while the header cell is hovered
 * @param {boolean}  [props.enableFillHandle=false] opt-in AG-Grid fill handle — select an editable cell and drag the
 *                                                 bottom-right square to copy its value into the cells below (or
 *                                                 alongside, per fillHandleDirection). Only text/currency/select/checkbox
 *                                                 cells are fillable; read-only/disabled cells and footer rows are skipped.
 *                                                 Filled values flow back through onRowChange just like a manual edit.
 * @param {('y'|'x'|'xy')} [props.fillHandleDirection='y'] fill drag axis — 'y' vertical (default), 'x' horizontal, 'xy' both
 * @param {Function} [props.onFillCells]         opt-in batched fill callback — receives every cell changed by a single
 *                                                 drag-fill as one array `[{ rowId, field, oldValue, newValue, data }]`,
 *                                                 flushed once per drag (microtask) instead of one onRowChange per cell.
 *                                                 Use this when a per-cell callback would race on a shared state clone.
 *                                                 When omitted, filled cells fall back to per-cell onRowChange.
 * @param {boolean}  [props.sortable=true]        table-wide default sortability for user columns; a column may still
 *                                                 override it with its own `sortable`. Set false to drop sorting
 *                                                 (and the kebab's Sort items) everywhere it isn't needed.
 * @param {boolean}  [props.showColumnChooser=true] when false, removes the "Choose Columns" item from the header
 *                                                 (3-dots) menu while leaving the rest of the menu intact
 * @param {boolean}  [props.suppressColumnResize=false] when true, makes every user column non-resizable and hides the
 *                                                 header resize handle + column separator line (opt-in; default keeps resizing on).
 * @param {boolean}  [props.fullWidthHorizontalScroll=false] opt-in: stretch the horizontal scrollbar across the full table
 *                                                 width (under the pinned columns) instead of only the scrollable centre
 *                                                 region. Pinned columns stay fixed; the scroll track runs edge-to-edge and
 *                                                 dragging the thumb still reaches the last column (the sizing container is
 *                                                 widened by the pinned widths so the scroll math stays correct).
 * @param {boolean}  [props.showColumnMenu=true]  when false, hides the header (3-dots) kebab button on every user
 *                                                 column. A column may still re-enable it with its own
 *                                                 `suppressHeaderMenuButton: false`.
 * @param {boolean}  [props.suppressTabNavigation=false] opt out of Tab/Shift+Tab moving the cursor between editable
 *                                                 cells. By default Tab advances focus to the next editable control
 *                                                 (and Shift+Tab to the previous) in row-major order; at the first/last
 *                                                 field focus leaves the grid normally.
 * @param {boolean}  [props.hideCellErrorMessage=false] when true, keeps the red error border on invalid cells
 *                                                 but suppresses the error message text rendered beneath them
 * @param {Function} [props.renderRowActionExtras] (params) => ReactNode. When the table has an action column
 *                                                 (onAddRow/onDeleteRow), the returned node is prepended inside
 *                                                 the action cell before the +/trash icons. Use to host an
 *                                                 inline control (e.g. Exclude checkbox) instead of a separate column.
 * @param {number}   [props.rowActionsWidth]      override the action column width (default 80px) — widen when
 *                                                 renderRowActionExtras adds a control next to the icons.
 * @param {object}   [props.gridOptions]         pass-through to AgGridReact
 */
function CommonAGGridTable({
  columnDefs = [],
  rowData = [],
  getRowId,
  onRowChange,
  onAddRow,
  onDeleteRow,
  readOnly = false,
  isRowReadOnly,
  canAddRow,
  canDeleteRow,
  getRowClass,
  pinnedBottomRowData,
  currencySymbol = '£',
  detailRowRenderer,
  isRowMaster,
  treeData = false,
  getDataPath,
  autoGroupColumnDef,
  groupDefaultExpanded = 0,
  radioGroupName,
  selectedRadioRowId,
  height,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  autoSizeColumns = false,
  revealHeaderControlsOnHover = false,
  enableFillHandle = false,
  fillHandleDirection = 'y',
  onFillCells,
  suppressTabNavigation = false,
  sortable = true,
  showColumnChooser = true,
  showColumnMenu = true,
  suppressColumnResize = false,
  fullWidthHorizontalScroll = false,
  hideCellErrorMessage = false,
  renderRowActionExtras,
  rowActionsWidth,
  gridOptions = EMPTY_GRID_OPTIONS,
}) {
  const gridRef = useRef(null);
  const wrapperRef = useRef(null);
  const fillBatchRef = useRef([]);
  const fillFlushScheduledRef = useRef(false);

  const resolveRowId = useCallback((row) => (getRowId ? getRowId(row) : row?.id), [getRowId]);

  /* Parents recreate these callbacks/values on every render — often once per keystroke,
     since their identity is derived from the row data being edited. Keep the latest in a
     ref and expose [] -stable wrappers so the column builder below never re-runs merely
     because a parent callback got a new identity. Re-running it makes AG-Grid reprocess
     every column on each keystroke, which is the dominant source of typing lag. */
  const handlersRef = useRef({});
  handlersRef.current = {
    onRowChange,
    resolveRowId,
    onAddRow,
    onDeleteRow,
    canAddRow,
    canDeleteRow,
    isRowReadOnly,
    onFillCells,
    renderRowActionExtras,
  };

  const stableOnRowChange = useCallback((rowId, field, value) => handlersRef.current.onRowChange?.(rowId, field, value), []);
  const stableResolveRowId = useCallback((row) => handlersRef.current.resolveRowId(row), []);
  const stableOnAddRow = useCallback((row) => handlersRef.current.onAddRow?.(row), []);
  const stableOnDeleteRow = useCallback((rowId) => handlersRef.current.onDeleteRow?.(rowId), []);
  const stableCanAddRow = useCallback((row) => (handlersRef.current.canAddRow ? handlersRef.current.canAddRow(row) : true), []);
  const stableCanDeleteRow = useCallback((row) => (handlersRef.current.canDeleteRow ? handlersRef.current.canDeleteRow(row) : true), []);
  const stableIsRowReadOnly = useCallback((row) => (handlersRef.current.isRowReadOnly ? handlersRef.current.isRowReadOnly(row) : false), []);

  /* Whether a trailing actions column exists is structural (it appears/disappears with
     the presence of the handlers), so the builder keys off this boolean rather than the
     volatile handler identities. */
  const hasRowActions = Boolean(onAddRow || onDeleteRow);

  const builtColumnDefs = useMemo(() => {
    const cols = [];

    const structuralColProps = {
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    };

    /* Tree-data mode uses AG-Grid's auto-group column for the chevron, so we
       must NOT inject our own expand column — otherwise two chevrons render. */
    if (detailRowRenderer && !treeData) {
      cols.push({
        colId: '__cag_expand',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        cellRenderer: ExpandChevronCell,
        pinned: 'left',
        ...structuralColProps,
      });
    }

    columnDefs.forEach((col) => {
      const {
        cellType,
        cellRenderer,
        cellProps = {},
        footerCellProps,
        sortable: columnSortable = sortable,
        getCellDisabled,
        getCellReadOnly,
        getCellError,
        getCellClass,
        fillable = false,
        ...rest
      } = col;

      const baseRenderer = cellRenderer || (cellType ? CELL_RENDERERS[cellType] : undefined);
      const isInteractive = cellType && cellType !== 'readonlyText' && cellType !== 'totalsFooter';

      /* Fill handle writes only to editable cells, so eligible columns expose an
         editable() callback that reuses the same read-only/disabled/footer guards as
         the renderer selector — a drag can never overwrite a locked cell or a footer.
         A custom-renderer column (no cellType) can still opt in with `fillable: true`
         as long as it declares a `field` for the fill bridge to read/write. */
      const isFillEligible = enableFillHandle && (fillable || (cellType && FILL_ELIGIBLE_CELL_TYPES.has(cellType)));
      const fillEditable = (params) => {
        const row = params?.data;
        if (isFooterRow(row)) return false;
        const rowReadOnly = readOnly || stableIsRowReadOnly(row);
        const cellReadOnly = getCellReadOnly ? getCellReadOnly(row) : false;
        const cellDisabled = getCellDisabled ? getCellDisabled(row) : false;
        return !rowReadOnly && !cellReadOnly && !cellDisabled;
      };

      /* Resolve per-row cell renderer + params so a "read-only" row swaps in
         ReadonlyTextCell, and disabled/error are evaluated against current row data. */
      const cellRendererSelector = baseRenderer
        ? (params) => {
            const row = params?.data;
            if (isFooterRow(row)) {
              /* Footer rows use TotalsFooterCell whenever the column declared footerCellProps
                 (or explicitly opted in via cellType: 'totalsFooter'). Other columns render blank. */
              if (footerCellProps || cellType === 'totalsFooter') {
                return { component: TotalsFooterCell };
              }
              return { component: undefined };
            }
            const rowReadOnly = readOnly || stableIsRowReadOnly(row);
            const cellReadOnly = rowReadOnly || (getCellReadOnly ? getCellReadOnly(row) : false);
            if (cellReadOnly && isInteractive) {
              return { component: ReadonlyTextCell };
            }
            return { component: baseRenderer };
          }
        : undefined;

      const cellRendererParams = baseRenderer
        ? (params) => {
            const row = params?.data;
            if (isFooterRow(row)) {
              return { ...cellProps, ...(footerCellProps || {}) };
            }
            const rowReadOnly = readOnly || stableIsRowReadOnly(row);
            return {
              onRowChange: stableOnRowChange,
              getRowId: stableResolveRowId,
              currencySymbol,
              groupName: radioGroupName,
              selectedRowId: selectedRadioRowId,
              ...cellProps,
              disabled: rowReadOnly || (getCellDisabled ? getCellDisabled(row) : cellProps.disabled),
              error: getCellError ? getCellError(row) : undefined,
            };
          }
        : undefined;

      const composedCellClass = (params) => {
        if (isFooterRow(params?.data)) return 'cag-cell-class-footer';
        const fromHook = getCellClass ? getCellClass(params?.data) : undefined;
        const fromColDef = typeof rest.cellClass === 'function' ? rest.cellClass(params) : rest.cellClass;
        const classes = [fromHook, fromColDef].filter(Boolean).flat();
        /* Tag cells the fill handle can never write to (non-eligible column, read-only
           row/cell, disabled, or footer) so CSS can suppress their range-selection
           highlight and the drag handle — the affordance only shows where a fill is valid. */
        if (enableFillHandle && !(isFillEligible && fillEditable(params))) classes.push('cag-cell-nofill');
        return classes;
      };

      /* When auto-sizing, drive width from the header label only (not cell content)
         so long read-only values can't blow a column out. A column that declares
         flex stays a fill column (absorbing leftover width so no empty band trails
         the table) and only has its minWidth raised to the header-fit floor.
         Columns with no header (structural) keep their declared width. */
      let headerWidthOverride = {};
      if (autoSizeColumns && rest.headerName) {
        const headerFit = headerFitWidth(rest.headerName, col.required, rest.minWidth);
        headerWidthOverride = rest.flex ? { minWidth: Math.max(rest.minWidth || 0, headerFit) } : { width: headerFit, flex: undefined };
      }

      cols.push({
        minWidth: 120,
        resizable: !suppressColumnResize,
        sortable: columnSortable,
        suppressHeaderMenuButton: !showColumnMenu,
        ...rest,
        ...headerWidthOverride,
        ...(isFillEligible ? { editable: fillEditable } : {}),
        cellClass: composedCellClass,
        cellRenderer: cellRendererSelector ? undefined : baseRenderer,
        cellRendererSelector,
        cellRendererParams,
      });
    });

    if (hasRowActions) {
      const actionsWidth = rowActionsWidth || 80;
      cols.push({
        colId: '__cag_actions',
        headerName: '',
        width: actionsWidth,
        minWidth: actionsWidth,
        maxWidth: Math.max(actionsWidth, 100),
        pinned: 'right',
        cellRenderer: RowActionsCell,
        cellRendererParams: (params) => {
          const row = params?.data;
          if (isFooterRow(row)) return {};
          const rowReadOnly = readOnly || stableIsRowReadOnly(row);
          const extras = handlersRef.current.renderRowActionExtras ? handlersRef.current.renderRowActionExtras({ ...params, rowReadOnly }) : null;
          return {
            /* Preserve the "handler present?" semantics RowActionsCell relies on to show
               each icon — pass the stable wrapper only when the parent actually supplied one. */
            onAddRow: handlersRef.current.onAddRow ? stableOnAddRow : undefined,
            onDeleteRow: handlersRef.current.onDeleteRow ? stableOnDeleteRow : undefined,
            getRowId: stableResolveRowId,
            canAdd: rowReadOnly ? false : stableCanAddRow(row),
            canDelete: rowReadOnly ? false : stableCanDeleteRow(row),
            extras,
          };
        },
        ...structuralColProps,
      });
    }

    return cols;
  }, [
    columnDefs,
    detailRowRenderer,
    hasRowActions,
    stableOnRowChange,
    stableResolveRowId,
    stableOnAddRow,
    stableOnDeleteRow,
    stableCanAddRow,
    stableCanDeleteRow,
    stableIsRowReadOnly,
    currencySymbol,
    radioGroupName,
    selectedRadioRowId,
    readOnly,
    treeData,
    autoSizeColumns,
    enableFillHandle,
    sortable,
    showColumnMenu,
    suppressColumnResize,
    rowActionsWidth,
  ]);

  const resolvedAutoGroupColumnDef = useMemo(() => {
    if (!treeData) return undefined;
    return {
      headerName: '',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: () => null,
      },
      ...(autoGroupColumnDef || {}),
    };
  }, [treeData, autoGroupColumnDef]);

  const defaultColDef = useMemo(
    () => ({
      suppressMovable: false,
      headerComponent: SortableHeader,
      tooltipValueGetter: getTruncatedCellTooltip,
      /* Our editable cells are always-on renderer inputs (not AG-Grid editors), so
         the grid's own key handling would otherwise swallow copy/select-all/caret
         navigation. Hand keyboard events back to the native input/select/textarea. */
      suppressKeyboardEvent: (params) => {
        const tag = params?.event?.target?.tagName;
        return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      },
    }),
    [],
  );

  /* The editable cells are always-on renderer inputs, not AG-Grid cell editors, so
     the grid never owns a "current cell" to tab between. Drive Tab ourselves: from
     the focused control, move to the next (or previous, on Shift) editable control
     in DOM order. At the first/last field we leave focus alone so Tab exits the grid. */
  const handleTabNavigation = useCallback(
    (event) => {
      if (suppressTabNavigation || event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
      const { target } = event;
      if (!target?.matches?.(TAB_TARGET_SELECTOR)) return;

      const controls = Array.from(event.currentTarget.querySelectorAll(TAB_TARGET_SELECTOR)).filter((el) => el === target || el.offsetParent !== null);
      const currentIndex = controls.indexOf(target);
      if (currentIndex === -1) return;

      const nextIndex = currentIndex + (event.shiftKey ? -1 : 1);
      if (nextIndex < 0 || nextIndex >= controls.length) return;

      event.preventDefault();
      const next = controls[nextIndex];
      next.focus();
      /* Show a caret at the end of the value rather than selecting it, so Tab lands
         the cursor in the field ready to type instead of highlighting the content. */
      const caret = next.value != null ? String(next.value).length : 0;
      if (typeof next.setSelectionRange === 'function') {
        try {
          next.setSelectionRange(caret, caret);
        } catch {
          /* Some input types (e.g. number) reject setSelectionRange — focus alone is fine. */
        }
      }
    },
    [suppressTabNavigation],
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    wrapper.addEventListener('keydown', handleTabNavigation);
    return () => wrapper.removeEventListener('keydown', handleTabNavigation);
  }, [handleTabNavigation]);

  const composedRowClass = useCallback(
    (params) => {
      if (isFooterRow(params?.data)) return 'cag-row-footer';
      return getRowClass ? getRowClass(params?.data) : '';
    },
    [getRowClass],
  );

  /* Bridge the fill handle to the controlled-edit pipeline: AG-Grid mutates data[field]
     during a drag-fill and fires this once per filled cell, which we forward to
     onRowChange so the value lands in consumer state exactly like a manual edit. */
  const handleFillValueChange = useCallback(
    (event) => {
      if (event?.newValue === event?.oldValue) return;
      const field = event?.colDef?.field;
      if (!field) return;
      const rowId = stableResolveRowId(event.data);
      const fillCells = handlersRef.current.onFillCells;
      /* A drag-fill fires this synchronously once per target cell. When onFillCells is
         supplied, collect the whole burst and flush it once on the next microtask so the
         consumer can apply every change to a single state clone (no stale-clone race). */
      if (fillCells) {
        fillBatchRef.current.push({ rowId, field, oldValue: event.oldValue, newValue: event.newValue, data: event.data });
        if (!fillFlushScheduledRef.current) {
          fillFlushScheduledRef.current = true;
          Promise.resolve().then(() => {
            const batch = fillBatchRef.current;
            fillBatchRef.current = [];
            fillFlushScheduledRef.current = false;
            if (batch.length) handlersRef.current.onFillCells?.(batch);
          });
        }
        return;
      }
      stableOnRowChange(rowId, field, event.newValue);
    },
    [stableOnRowChange, stableResolveRowId],
  );

  /* Stable row-id accessor for AG-Grid itself — a fresh identity here would defeat the
     grid's immutable-update path and force more work on each data change. */
  const getGridRowId = useCallback((params) => String(stableResolveRowId(params.data)), [stableResolveRowId]);

  /* AG-Grid's immutable update (getRowId) only refreshes cells whose value
     changed, so an out-of-band change — a validation error surfaced via
     getCellError, a toggle whose state isn't the cell value, or a cross-field
     derived display (cell A's calc depends on cell B) — is never repainted.
     Force a refresh when rowData changes. When a grid control is focused we
     cannot force-refresh that cell (our renderers have no AG-Grid refresh()
     method, so a force refresh remounts the input and drops the caret), but we
     still refresh every OTHER cell so sibling/derived cells repaint live while
     the user types. Only the focused cell is spared. A control focused outside
     this grid (e.g. an exchange-rate input above the table, or a sibling grid)
     has no caret of ours to protect, so we refresh everything. */
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || api.isDestroyed?.()) return;
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const activeTag = active?.tagName;
    const isEditing = activeTag === 'INPUT' || activeTag === 'SELECT' || activeTag === 'TEXTAREA';
    const focusInThisGrid = isEditing && wrapperRef.current?.contains(active);
    const focusedColId = focusInThisGrid ? active.closest?.('.ag-cell')?.getAttribute('col-id') : undefined;
    if (!focusedColId) {
      api.refreshCells({ force: true });
      return;
    }
    const otherColumns = (api.getColumns?.() || []).map((column) => column.getColId()).filter((colId) => colId !== focusedColId);
    if (otherColumns.length) api.refreshCells({ force: true, columns: otherColumns });
  }, [rowData]);

  /* Full-width horizontal scrollbar bridge. AG-Grid pins the horizontal scroll track
     to the scrollable (centre, non-pinned) region: the left/right spacers reserve the
     pinned widths and the sizing container is only as wide as the centre content.
     To run the track edge-to-edge we (a) collapse the spacers to 0 via CSS so the
     flex:1 viewport fills the full width, and (b) widen the sizing container to the
     total of ALL displayed columns (centre + pinned) here. Because the target is
     derived from the column model — not the current DOM width — this is idempotent:
     AG-Grid resetting the width to centre-only just triggers a clean re-apply, never a
     runaway. The extra pinned-width tail is invisible sizing, so the thumb still hits
     the true end exactly when the last data column comes into view. */
  const applyFullWidthHScroll = useCallback(() => {
    const api = gridRef.current?.api;
    const wrapper = wrapperRef.current;
    if (!api || api.isDestroyed?.() || !wrapper) return;
    const container = wrapper.querySelector('.ag-body-horizontal-scroll-container');
    if (!container) return;
    const displayed = [...(api.getDisplayedLeftColumns?.() || []), ...(api.getDisplayedCenterColumns?.() || []), ...(api.getDisplayedRightColumns?.() || [])];
    const totalWidth = displayed.reduce((sum, column) => sum + (column.getActualWidth?.() || 0), 0);
    if (totalWidth > 0) container.style.width = `${totalWidth}px`;
  }, []);

  /* Opt-in continuous infinite scroll: when onLoadMore is supplied, fire it once
     the body scroll settles near the bottom and there is another page to load.
     Additive — absent props leave existing consumers untouched. */
  const mergedGridOptions = useMemo(() => {
    if (!onLoadMore && !enableFillHandle && !fullWidthHorizontalScroll) return gridOptions;
    const merged = { ...gridOptions };

    if (onLoadMore) {
      /* Prefetch the next page once the viewport bottom passes this fraction of the loaded
         content height (0.75 = 25% still below the fold). Firing ahead of the absolute
         bottom hides the network round-trip so scrolling stays smooth. */
      const LOAD_MORE_SCROLL_RATIO = 0.75;
      merged.onBodyScroll = (params) => {
        gridOptions.onBodyScroll?.(params);
        /* onBodyScroll fires continuously DURING the scroll (unlike onBodyScrollEnd, which
           waits for the scroll to settle), so the prefetch lands mid-scroll instead of only
           after the user stops at the bottom. Ignore horizontal scroll. */
        if (params.direction === 'horizontal') return;
        if (!hasMore || loadingMore) return;
        const range = params.api.getVerticalPixelRange();
        if (!range) return;
        const contentHeight = params.api.getDisplayedRowCount() * 40;
        if (contentHeight <= 0) return;
        const viewportHeight = range.bottom - range.top;
        const contentFillsViewport = contentHeight > viewportHeight;
        const passedThreshold = range.bottom >= contentHeight * LOAD_MORE_SCROLL_RATIO;
        /* Load when the user has scrolled DOWN past the threshold (range.top > 0), OR when the
           loaded rows do not yet fill the viewport (auto-fill on tall screens). The range.top
           gate stops the grid's initial layout scroll from pre-loading page 2 before any user
           scroll — a full page (~20 rows) already meets the threshold while pinned at the top.
           The synchronous in-flight guard in the consumer keeps the continuous events from
           firing more than one request per page. */
        if (passedThreshold && (range.top > 0 || !contentFillsViewport)) onLoadMore();
      };
    }

    /* Fill handle: enable cell-range selection with the fill square, keep the
       always-on input cells the sole editor (suppressClickEdit), and fan filled
       values out through onRowChange. A caller may still override any of these
       via gridOptions if they need bespoke behaviour. */
    if (enableFillHandle) {
      merged.cellSelection = gridOptions.cellSelection ?? { handle: { mode: 'fill', direction: fillHandleDirection } };
      merged.suppressClickEdit = gridOptions.suppressClickEdit ?? true;
      merged.onCellValueChanged = (event) => {
        gridOptions.onCellValueChanged?.(event);
        handleFillValueChange(event);
      };
    }

    /* Re-apply the full-width container sizing after every AG-Grid layout pass that
       can change column widths or the scroll DOM, chaining any caller-supplied handler
       first so we never clobber it. */
    if (fullWidthHorizontalScroll) {
      const chain = (userHandler) => (event) => {
        userHandler?.(event);
        applyFullWidthHScroll();
      };
      merged.onFirstDataRendered = chain(gridOptions.onFirstDataRendered);
      merged.onDisplayedColumnsChanged = chain(gridOptions.onDisplayedColumnsChanged);
      merged.onColumnResized = chain(gridOptions.onColumnResized);
      merged.onGridSizeChanged = chain(gridOptions.onGridSizeChanged);
      merged.onVirtualColumnsChanged = chain(gridOptions.onVirtualColumnsChanged);
    }

    return merged;
  }, [
    onLoadMore,
    hasMore,
    loadingMore,
    gridOptions,
    enableFillHandle,
    fillHandleDirection,
    handleFillValueChange,
    fullWidthHorizontalScroll,
    applyFullWidthHScroll,
  ]);

  const useAutoHeight = !height;
  const wrapperClass = [
    'cag-wrapper',
    useAutoHeight ? 'cag-wrapper--auto' : '',
    revealHeaderControlsOnHover ? 'cag-wrapper--hover-controls' : '',
    suppressColumnResize ? 'cag-wrapper--no-resize' : '',
    fullWidthHorizontalScroll ? 'cag-wrapper--full-hscroll' : '',
    hideCellErrorMessage ? 'cag-wrapper--no-cell-error' : '',
  ]
    .filter(Boolean)
    .join(' ');
  /* Dynamic, caller-supplied height is the documented exception to the
     no-inline-style rule (see feedback_no_inline_styles.md). */
  const themeStyle = useAutoHeight ? undefined : { height };

  /* Strip the "Choose Columns" entry from the header (3-dots) menu when the caller
     opts out, leaving every other default menu item (pin/autosize/reset/sort) intact. */
  const getMainMenuItems = useCallback((params) => params.defaultItems.filter((item) => item !== 'columnChooser'), []);

  return (
    <div className={wrapperClass} ref={wrapperRef}>
      <div className="ag-theme-alpine cag-grid-theme" style={themeStyle}>
        <AgGridReact
          ref={gridRef}
          theme="legacy"
          columnDefs={builtColumnDefs}
          rowData={rowData}
          pinnedBottomRowData={pinnedBottomRowData}
          defaultColDef={defaultColDef}
          getRowId={getGridRowId}
          getRowClass={composedRowClass}
          masterDetail={Boolean(detailRowRenderer) && !treeData}
          detailCellRenderer={!treeData ? detailRowRenderer : undefined}
          isRowMaster={!treeData ? isRowMaster || (detailRowRenderer ? () => true : undefined) : undefined}
          treeData={treeData}
          getDataPath={treeData ? getDataPath : undefined}
          autoGroupColumnDef={resolvedAutoGroupColumnDef}
          groupDefaultExpanded={treeData ? groupDefaultExpanded : undefined}
          domLayout={useAutoHeight ? 'autoHeight' : undefined}
          headerHeight={40}
          rowHeight={40}
          animateRows
          suppressDragLeaveHidesColumns
          suppressCellFocus={!enableFillHandle}
          getMainMenuItems={showColumnChooser ? undefined : getMainMenuItems}
          tooltipShowMode="whenTruncated"
          tooltipShowDelay={GRID_TOOLTIP_SHOW_DELAY_MS}
          {...mergedGridOptions}
        />
        {loadingMore && (
          <div className="cag-loading-overlay" aria-live="polite" aria-busy="true">
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
}

export default CommonAGGridTable;
